import {
  Box,
  Grid,
  GridItem,
  HStack,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { RouterLink } from '@gamma/ui/navigation';
import camelcase from 'lodash/camelCase';
import { FC, useContext, useMemo, useRef } from 'react';
import { ROUTES } from '@investigator/shared/constants';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import formatUnixTimeStampAsDisplayString from '@investigator/shared/utils/formatUnixTimeStampAsDisplayString';
import {
  DetailsGridRow,
  MitreTechniquesGridTruncated,
} from '../../../../../components';
import { safelySetInnerHTML } from '../../../DetectionDetails';
import { AnomalyPanel } from '../../AnomalyPanel';
import { LayoutProps } from '../types';
import { stringifyAlertType } from '@investigator/shared/utils';
import { OrgTenantsContext } from '@investigator/shared/providers/OrgTenantsProvider/OrgTenantsContext';

const { detectionSummary, detection: detectionText } = i18n.pages.detections;

const { systemSettingsAlertCatalog } = ROUTES;

type AnomalyType =
  | 'anomalousServiceOnInternetFacingServer'
  | 'anomalousSshClientOnSubnet'
  | 'anomalousHttpUserAgentFamilyIdentifiedOnSubnet'
  | 'anomalousServiceIdentifiedBetweenInternalSubnets'
  | 'anomalousSshDestinationFromInternalSubnet';

const Anomaly: FC<LayoutProps> = ({
  detectionInfo,
  metadata,
  isExpanded,
  setIsExpanded,
}) => {
  const { start, end } = useDateRangeQueryString();
  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);

  const {
    alertCategory,
    numberOfAlerts,
    type,
    firstTimeSeen,
    lastTimeSeen,
    description: localeDesc,
    significance,
    validation,
    mitreTechniques,
  } = detectionText;

  const {
    alert_info,
    total_alert_count,
    earliest_start_timestamp,
    latest_start_timestamp,
  } = detectionInfo;

  const {
    description = '',
    mitre_mappings: metadata_mitre_mappings,
    content_doc,
  } = metadata;

  const { anomalyCopy, seeMore, seeLess } = detectionSummary;

  const anomalyType = useMemo(
    () => camelcase(alert_info?.alert_name as string),
    [alert_info?.alert_name],
  );

  const anomalyTranslations = useMemo(
    () => anomalyCopy[(anomalyType as AnomalyType) || undefined],
    [anomalyCopy, anomalyType],
  );

  const validationRef = useRef<HTMLDivElement>(null);

  const hasMetaContent = !!(description || content_doc?.significance);

  return (
    <HStack align="start">
      <Box flex="1">
        <VStack alignItems="start">
          <HStack w="100%" alignItems="start">
            <VStack flex="1" alignItems="start">
              {alert_info?.alert_name && (
                <Grid templateColumns="1fr" width="100%">
                  <GridItem>
                    <Text color="text.secondary">{alertCategory}</Text>
                  </GridItem>
                  <GridItem
                    data-testid="detection-summary-alert-name-value"
                    overflow="hidden"
                    minW={0}
                  >
                    <RouterLink
                      to={`${systemSettingsAlertCatalog(
                        `/${alert_info?.content_id}?start=${start}&end=${end}&tenant=${orgTenantsQueryParam}`,
                      )}`}
                      data-testid="pivot-detection-alert-catalog-link"
                      color="text.link"
                    >
                      {/* Can't use the TruncatedText component here because it is inside a <RouterLink> */}
                      {alert_info?.alert_name?.length > 25
                        ? alert_info?.alert_name.slice(0, 25 - 1) + '…'
                        : alert_info?.alert_name}
                    </RouterLink>
                  </GridItem>
                </Grid>
              )}
              {total_alert_count !== null && (
                <DetailsGridRow
                  isDetailsPage
                  title={numberOfAlerts}
                  data-testid="detection-summary-alert-count"
                >
                  <Text>{total_alert_count}</Text>
                </DetailsGridRow>
              )}
            </VStack>
            <VStack flex="1" alignItems="start">
              {alert_info?.alert_type && (
                <DetailsGridRow
                  title={type}
                  isDetailsPage
                  data-testid="detection-summary-type"
                >
                  <Text>{stringifyAlertType(alert_info?.alert_type)}</Text>
                </DetailsGridRow>
              )}
              {earliest_start_timestamp && (
                <DetailsGridRow
                  isDetailsPage
                  title={firstTimeSeen}
                  data-testid="detection-summary-first-timestamp"
                >
                  <Text>
                    {formatUnixTimeStampAsDisplayString(
                      earliest_start_timestamp,
                    )}
                  </Text>
                </DetailsGridRow>
              )}
            </VStack>
            <VStack flex="1" alignItems="start">
              {!!metadata_mitre_mappings?.tactics?.length && (
                <VStack alignItems="start">
                  <Text
                    color="text.secondary"
                    data-testid={'metadata_mitre_mappings-label'}
                  >
                    {mitreTechniques}
                  </Text>
                  <MitreTechniquesGridTruncated
                    tactics={metadata_mitre_mappings?.tactics}
                  />
                </VStack>
              )}
              {latest_start_timestamp && (
                <DetailsGridRow
                  isDetailsPage
                  title={lastTimeSeen}
                  data-testid="detection-summary-last-timestamp"
                >
                  <Text>
                    {formatUnixTimeStampAsDisplayString(latest_start_timestamp)}
                  </Text>
                </DetailsGridRow>
              )}
            </VStack>
          </HStack>
          {hasMetaContent &&
            (description?.length ?? 0) > 0 &&
            alert_info?.alert_type !== 'suricata_corelight' && (
              <DetailsGridRow
                isDetailsPage
                title={localeDesc}
                data-testid="detection-summary-description"
              >
                <Text>{anomalyTranslations?.description ?? description}</Text>
              </DetailsGridRow>
            )}
          {hasMetaContent && content_doc?.significance && (
            <DetailsGridRow
              isDetailsPage
              title={significance}
              data-testid="detection-summary-significance"
            >
              <Text as="div">{content_doc.significance}</Text>
            </DetailsGridRow>
          )}
          {isExpanded && hasMetaContent && content_doc?.validation && (
            <DetailsGridRow
              isDetailsPage
              title={validation}
              data-testid="detection-content-doc-validation"
            >
              <Box
                ref={validationRef}
                {...safelySetInnerHTML(content_doc?.validation)}
                __css={{
                  ul: {
                    'margin-left': '16px',
                  },
                }}
              />
            </DetailsGridRow>
          )}
          {content_doc?.validation && (
            <Link color="text.link" onClick={() => setIsExpanded(!isExpanded)}>
              {!isExpanded ? seeMore : seeLess}
            </Link>
          )}
        </VStack>
      </Box>
      <Box flex="1">{detectionInfo && <AnomalyPanel />}</Box>
    </HStack>
  );
};

export default Anomaly;
